import { css } from "../helpers/webComponents";

const styles: string = css`
    :host {
        background-color: #1D2434;
        border-top: 3px solid #3B4B62;
        color: white;
        display: flex;
        flex-direction: column;
        padding: 10px;
        position: fixed;
        z-index: 1;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 33%;
        height: 50%;
    }

    .chemical-header {
        font-size: 1.2em;
        margin-bottom: 10px;
    }

    .button {
        padding: 10px;
        margin: 5px 0;
        cursor: pointer;
        background-color: #3B4B62;
        border: none;
        color: white;
        text-align: center;
        border-radius: 5px;
    }

    .button.selected {
        background-color: #78808D;
    }
`;

/**
 * Custom element for mixing chemicals.
 */
export class MixChemicalsComponent extends HTMLElement {
    private selectedBottles: number[] = [];
    private correctOrder: number[] = [5, 6, 4, 3];

    /**
     * Constructor for the MixChemicalsComponent.
     */
    public constructor() {
        super();
        this.attachShadow({ mode: "open" });
    }

    /**
     * Called when the element is connected to the DOM.
     */
    public connectedCallback(): void {
        this.render();
    }

    /**
     * Handles the click event on a bottle button.
     * @param bottleId - The ID of the clicked bottle.
     */
    private handleBottleClick(bottleId: number): void {
        if (this.selectedBottles.length < 4) {
            this.selectedBottles.push(bottleId);
            this.render();

            if (this.selectedBottles.length === 4) {
                this.checkCorrectMix();
            }
        }
    }

    /**
     * Checks if the selected bottles are in the correct order.
     */
    private checkCorrectMix(): void {
        const isCorrect: boolean = this.selectedBottles.every((id, index) => id === this.correctOrder[index]);

        if (isCorrect) {
            this.dispatchEvent(new CustomEvent("mix-success", { bubbles: true, composed: true }));
            this.style.display = "none";
        }
        else {
            this.selectedBottles = [];
            this.render();
        }
    }

    /**
     * Renders the component.
     */
    private render(): void {
        if (!this.shadowRoot) return;

        this.shadowRoot.innerHTML = `
            <style>${styles}</style>
            <div class="chemical-header">Select four bottles to mix:</div>
            ${[3, 4, 5, 6].map(id => `
                <button class="button ${this.selectedBottles.includes(id) ? "selected" : ""}" data-id="${id}">
                    ${this.getLabel(id)}
                </button>
            `).join("")}
        `;

        this.shadowRoot.querySelectorAll("button[data-id]").forEach(button => {
            const id: number = parseInt(button.getAttribute("data-id")!);
            button.addEventListener("click", () => this.handleBottleClick(id));
        });
    }

    /**
     * Gets the label for a given bottle ID.
     * @param id - The ID of the bottle.
     * @returns The label of the bottle.
     */
    private getLabel(id: number): string {
        switch (id) {
            case 3: return "Blue";
            case 4: return "Green";
            case 5: return "Red";
            case 6: return "Yellow";
            default: return "Unknown";
        }
    }
}

customElements.define("mix-chemicals", MixChemicalsComponent);
